import { ResponseArray, ResponseObject } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import qs from 'query-string'
import {
    BestEmployeeContactsItem,
    BestEmployeeConversionItem,
    ConversionFunnelAvgDays,
    ConversionFunnelResult,
    ConversionFunnelSummary,
    ConversionFunnelSummaryAPI,
    ConversionFunnelRawFilters,
    ConversionFunnelRawResult,
    ConversionRates,
    CustomerPipelineFilters,
    DailyInputItem,
    EmployeeConversion,
    SalesPerformanceItem,
    StatusTransitionItem,
    TransitionTimelineResult,
    QualifiedProspectsFilters,
    QualifiedProspectsOverviewResult,
    SalesActivityResult,
    PipelineAnalysisResult,
    CustomerSummaryResult,
    CustomersByIndustryResult,
    CustomersByProductResult,
} from './types'

const BASE_URL = '/reports/bi/customer-pipeline'

// 1. Sales Performance Report
export const getSalesPerformance = async (filters: CustomerPipelineFilters) => {
    const response = await datacentral.get<ResponseArray<SalesPerformanceItem>>(
        `${BASE_URL}/sales-performance?${qs.stringify(filters)}`
    )
    return response.data.results
}

// API Response type with nested structure
interface ConversionFunnelAPIResult {
    summary: ConversionFunnelSummaryAPI
    conversion_rates: ConversionRates
    avg_days: ConversionFunnelAvgDays
    by_employee: EmployeeConversion[]
}

// Transform nested API response to flattened structure for components
const transformConversionFunnelResponse = (
    apiResult: ConversionFunnelAPIResult
): ConversionFunnelResult => {
    const { summary, conversion_rates, avg_days, by_employee } = apiResult

    const flattenedSummary: ConversionFunnelSummary = {
        total_new_contacts: summary.new_contacts,

        total_searching_records: summary.searching.records,
        total_searching: summary.searching.unique,

        total_presentation_records: summary.presentation.records,
        total_presentation: summary.presentation.unique,
        presentation_from_new_contacts: summary.presentation.from_new,
        presentation_from_old_contacts: summary.presentation.from_old,

        total_poc_records: summary.poc.records,
        total_poc: summary.poc.unique,
        poc_from_new_contacts: summary.poc.from_new,
        poc_from_old_contacts: summary.poc.from_old,

        total_contract_records: summary.contract.records,
        total_contract: summary.contract.unique,
        contract_from_new_contacts: summary.contract.from_new,
        contract_from_old_contacts: summary.contract.from_old,

        total_close_win_records: summary.close_win.records,
        total_close_win: summary.close_win.unique,
        close_win_from_new_contacts: summary.close_win.from_new,
        close_win_from_old_contacts: summary.close_win.from_old,

        total_close_lose_records: summary.close_lose.records,
        total_close_lose: summary.close_lose.unique,
        close_lose_from_new_contacts: summary.close_lose.from_new,
        close_lose_from_old_contacts: summary.close_lose.from_old,
    }

    return {
        summary: flattenedSummary,
        conversion_rates,
        avg_days,
        by_employee,
    }
}

// 2. Conversion Funnel Report
export const getConversionFunnel = async (filters: CustomerPipelineFilters) => {
    const response = await datacentral.get<
        ResponseObject<ConversionFunnelAPIResult>
    >(`${BASE_URL}/conversion-funnel?${qs.stringify(filters)}`)
    return transformConversionFunnelResponse(response.data.result)
}

// 3. Transition Timeline Report
export const getTransitionTimeline = async (
    filters: CustomerPipelineFilters & { customer_id?: number }
) => {
    const response = await datacentral.get<
        ResponseObject<TransitionTimelineResult>
    >(`${BASE_URL}/transition-timeline?${qs.stringify(filters)}`)
    return response.data.result
}

// 4. Daily Input Report
export const getDailyInput = async (filters: CustomerPipelineFilters) => {
    const response = await datacentral.get<ResponseArray<DailyInputItem>>(
        `${BASE_URL}/daily-input?${qs.stringify(filters)}`
    )
    return response.data.results
}

// 5. Status Transitions Report
export const getStatusTransitions = async (
    filters: CustomerPipelineFilters
) => {
    const response = await datacentral.get<ResponseArray<StatusTransitionItem>>(
        `${BASE_URL}/status-transitions?${qs.stringify(filters)}`
    )
    return response.data.results
}

// 6. Best Employees - Contacts Input
export const getBestEmployeesContacts = async (
    filters: Pick<CustomerPipelineFilters, 'gte' | 'lte'> & { limit?: number }
) => {
    const response = await datacentral.get<
        ResponseArray<BestEmployeeContactsItem>
    >(`${BASE_URL}/best-employees/contacts-input?${qs.stringify(filters)}`)
    return response.data.results
}

// 7. Best Employees - Conversion
export const getBestEmployeesConversion = async (
    filters: Pick<CustomerPipelineFilters, 'gte' | 'lte'> & { limit?: number }
) => {
    const response = await datacentral.get<
        ResponseArray<BestEmployeeConversionItem>
    >(`${BASE_URL}/best-employees/conversion?${qs.stringify(filters)}`)
    return response.data.results
}

// 8. Export Endpoints
export const exportDailyInput = async (
    filters: Pick<CustomerPipelineFilters, 'gte' | 'lte'>
) => {
    const response = await datacentral.get(
        `${BASE_URL}/export/daily-input?${qs.stringify(filters)}`,
        { responseType: 'blob' }
    )
    return response.data
}

export const exportBestEmployeesContacts = async (
    filters: Pick<CustomerPipelineFilters, 'gte' | 'lte'> & { limit?: number }
) => {
    const response = await datacentral.get(
        `${BASE_URL}/export/best-employees-contacts?${qs.stringify(filters)}`,
        { responseType: 'blob' }
    )
    return response.data
}

export const exportBestEmployeesConversion = async (
    filters: Pick<CustomerPipelineFilters, 'gte' | 'lte'> & { limit?: number }
) => {
    const response = await datacentral.get(
        `${BASE_URL}/export/best-employees-conversion?${qs.stringify(filters)}`,
        { responseType: 'blob' }
    )
    return response.data
}

// ============================================
// Qualified Prospects Services
// ============================================

const QUALIFIED_PROSPECTS_URL = '/reports/qualified-prospects'

// 1. Overview
export const getQualifiedProspectsOverview = async (
    filters: QualifiedProspectsFilters
) => {
    const response = await datacentral.get<
        ResponseObject<QualifiedProspectsOverviewResult>
    >(`${QUALIFIED_PROSPECTS_URL}/overview?${qs.stringify(filters)}`)
    return response.data.result
}

// 2. Sales Activity
export const getSalesActivity = async (filters: QualifiedProspectsFilters) => {
    const response = await datacentral.get<ResponseObject<SalesActivityResult>>(
        `${QUALIFIED_PROSPECTS_URL}/sales-activity?${qs.stringify(filters)}`
    )
    return response.data.result
}

// 3. Pipeline Analysis
export const getPipelineAnalysis = async (
    filters: QualifiedProspectsFilters
) => {
    const response = await datacentral.get<
        ResponseObject<PipelineAnalysisResult>
    >(`${QUALIFIED_PROSPECTS_URL}/pipeline?${qs.stringify(filters)}`)
    return response.data.result
}

// ============================================
// Conversion Funnel Raw Data
// ============================================

// Get raw conversion funnel data (JSON)
export const getConversionFunnelRaw = async (
    filters: ConversionFunnelRawFilters
) => {
    const response = await datacentral.get<
        ResponseObject<ConversionFunnelRawResult>
    >(`${BASE_URL}/conversion-funnel-raw?${qs.stringify(filters)}`)
    return response.data.result
}

// ============================================
// Customer Summary & Industry Reports
// ============================================

// 8. Customers By Industry (for Radar Chart)
export const getCustomersByIndustry = async (
    filters: CustomerPipelineFilters
) => {
    const response = await datacentral.get<
        ResponseObject<CustomersByIndustryResult>
    >(`${BASE_URL}/by-industry?${qs.stringify(filters)}`)
    return response.data.result
}

// 9. Customer Summary Report (stats cards)
export const getCustomerSummary = async (filters: CustomerPipelineFilters) => {
    const response = await datacentral.get<
        ResponseObject<CustomerSummaryResult>
    >(`${BASE_URL}/summary?${qs.stringify(filters)}`)
    return response.data.result
}

// 10. Customers By Product (for Bar Chart)
export const getCustomersByProduct = async (
    filters: CustomerPipelineFilters
) => {
    const response = await datacentral.get<
        ResponseObject<CustomersByProductResult>
    >(`${BASE_URL}/by-product?${qs.stringify(filters)}`)
    return response.data.result
}
